import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  CalendarOff,
  EyeOff,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  X,
} from "lucide-react";
import { moderationApi } from "./api";
import type {
  ModerationAction,
  ModerationDetailResponse,
  ModerationEventRow,
  ModerationFilters,
  ModerationStatus,
} from "./types";
import { StatusBadge } from "../applications/shared/StatusBadge";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field } from "../applications/shared/InfoBlock";
import {
  formatApplicantName,
  formatDate,
  formatDateTime,
} from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import {
  MODERATION_STATUS_LABELS,
  moderationStatusTone,
} from "./shared/labels";
import { useToast } from "@/app/providers/Toaster";
import { useDebounce } from "@/lib/utils/useDebounce";
import { confirmAction } from "@/app/providers/ConfirmDialog";

const STATUS_OPTIONS = [
  { value: "NONE", label: "Sin acción" },
  { value: "UNPUBLISHED", label: "Despublicado" },
  { value: "FROZEN", label: "Ventas pausadas" },
  { value: "CANCELED", label: "Cancelado" },
  { value: "BANNED", label: "Baneado" },
];

const PAGE_SIZE = 25;

export function ModerationScreen() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, status]);

  const filters: ModerationFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      moderationStatus: (status || undefined) as ModerationStatus | undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, status, page],
  );

  const listQuery = useQuery({
    queryKey: ["governance", "moderation", filters],
    queryFn: () => moderationApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ["governance", "moderation", "detail", selectedId],
    queryFn: () => moderationApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Governance · Moderación</p>
          <h1 className="nx-h1">Moderación de eventos</h1>
          <p className="text-sm text-fg-muted mt-1">
            Acciones de plataforma sobre eventos del productor. Cada acción
            requiere motivo, queda registrada en el log inmutable y dispara
            email + notificación al productor.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por evento, club o email del productor…"
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando eventos…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<ModerationEventRow>
          rows={listQuery.data?.items ?? []}
          columns={columns}
          page={listQuery.data?.page ?? 1}
          pageSize={listQuery.data?.pageSize ?? PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
        />
      )}

      {selectedId !== null && (
        <ModerationDrawer
          eventId={selectedId}
          data={detailQuery.data ?? null}
          loading={detailQuery.isLoading}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

const columns: Column<ModerationEventRow>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "event",
    header: "Evento",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.nombre}</div>
        <div className="text-2xs text-fg-subtle">{r.club?.name || r.club?.nombre || "—"}</div>
      </div>
    ),
  },
  {
    key: "producer",
    header: "Productor",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{formatApplicantName(r.usuario)}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.usuario?.email ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "fecha",
    header: "Fecha",
    cell: (r) => <span className="text-fg-muted text-xs">{formatDate(r.fecha)}</span>,
    width: "min",
  },
  {
    key: "lifecycle",
    header: "Lifecycle",
    cell: (r) => <span className="text-fg-subtle text-xs">{r.status}</span>,
    width: "min",
  },
  {
    key: "moderation",
    header: "Moderación",
    cell: (r) => (
      <StatusBadge
        status={r.moderationStatus}
        tone={moderationStatusTone(r.moderationStatus)}
        label={MODERATION_STATUS_LABELS[r.moderationStatus]}
      />
    ),
    width: "min",
  },
];

interface DrawerProps {
  eventId: number;
  data: ModerationDetailResponse | null;
  loading: boolean;
  onClose: () => void;
}

function ModerationDrawer({ eventId, data, loading, onClose }: DrawerProps) {
  const qc = useQueryClient();
  const { push } = useToast();

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["governance", "moderation"] });
  };

  const apply = useMutation({
    mutationFn: ({ action, reason }: { action: ModerationAction; reason: string }) =>
      moderationApi.apply(eventId, action, reason),
    onSuccess: (res) => {
      const w = res.warnings?.length ? ` · ${res.warnings.join(", ")}` : "";
      push({ kind: "success", message: `Acción aplicada${w}` });
      invalidate();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || "No se pudo aplicar la acción.";
      push({ kind: "error", message: msg });
    },
  });

  const runAction = async (
    action: ModerationAction,
    config: { title: string; tone: "danger" | "success"; warning?: string },
  ) => {
    const reason = await confirmAction({
      title: config.title,
      message: config.warning,
      promptLabel: "Motivo (obligatorio, queda en el log y el email al productor)",
      confirmLabel: "Confirmar",
      tone: config.tone,
    });
    if (reason === null) return;
    const trimmed = (reason as string).trim();
    if (!trimmed) {
      push({ kind: "error", message: "El motivo es obligatorio." });
      return;
    }
    apply.mutate({ action, reason: trimmed });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[640px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">{data?.event.nombre ?? "Evento"}</h2>
            {data && (
              <p className="text-2xs text-fg-subtle mt-0.5">
                #{data.event.id} · {data.event.club?.name || data.event.club?.nombre || "—"}
              </p>
            )}
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !data ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando detalle…
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge
                  status={data.event.moderationStatus}
                  tone={moderationStatusTone(data.event.moderationStatus)}
                  label={MODERATION_STATUS_LABELS[data.event.moderationStatus]}
                />
                <span className="text-2xs text-fg-subtle">
                  Lifecycle: <strong className="text-fg-muted">{data.event.status}</strong>
                </span>
              </div>

              {data.activeTicketHolders > 0 &&
                (data.event.moderationStatus === "NONE" || data.event.moderationStatus === "UNPUBLISHED") && (
                  <div className="flex items-start gap-2 text-xs p-3 rounded-md border border-warning/40 bg-warning-soft text-fg">
                    <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
                    <span>
                      <strong>{data.activeTicketHolders.toLocaleString("es-CL")}</strong> tickets
                      vendidos activos. Acciones como cancelar o banear afectan a estos compradores
                      (los reembolsos los gestiona el productor).
                    </span>
                  </div>
                )}

              <Section title="Evento">
                <Field label="Nombre" value={data.event.nombre} wide />
                <Field label="Club" value={data.event.club?.name || data.event.club?.nombre || "—"} />
                <Field label="Fecha" value={formatDate(data.event.fecha)} />
                <Field label="Aforo" value={data.event.aforo_total?.toLocaleString("es-CL") ?? "—"} />
                <Field label="Tickets activos" value={data.activeTicketHolders.toLocaleString("es-CL")} />
                <Field label="Productor" value={formatApplicantName(data.event.usuario)} wide />
                <Field
                  label="Email productor"
                  value={
                    data.event.usuario?.email ? (
                      <a className="text-accent hover:underline" href={`mailto:${data.event.usuario.email}`}>
                        {data.event.usuario.email}
                      </a>
                    ) : "—"
                  }
                  wide
                />
              </Section>

              {data.event.moderationReason && (
                <Section title="Motivo actual">
                  <Field
                    label="Razón registrada"
                    value={
                      <p className="whitespace-pre-wrap text-fg">{data.event.moderationReason}</p>
                    }
                    wide
                  />
                  {data.event.moderatedAt && (
                    <Field label="Aplicada" value={formatDateTime(data.event.moderatedAt)} />
                  )}
                </Section>
              )}

              {data.logs.length > 0 && (
                <Section title={`Bitácora (${data.logs.length})`}>
                  <div className="col-span-2 flex flex-col gap-2">
                    {data.logs.map((log) => (
                      <div
                        key={log.id}
                        className="border border-border-subtle rounded-md p-2.5 text-xs"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-fg">{log.action}</span>
                          <span className="text-2xs text-fg-subtle">
                            {formatDateTime(log.createdAt)}
                          </span>
                        </div>
                        <div className="text-2xs text-fg-subtle mb-1">
                          {log.fromStatus ?? "—"} → {log.toStatus ?? "—"} ·{" "}
                          {log.admin ? formatApplicantName(log.admin) : "Sistema"}
                        </div>
                        <div className="text-fg-muted whitespace-pre-wrap">{log.reason}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}
        </div>

        {data && (
          <footer className="px-5 py-3 border-t border-border bg-bg-surface flex flex-wrap items-center gap-2 justify-end">
            <ActionButtons
              status={data.event.moderationStatus}
              pending={apply.isPending}
              onAction={runAction}
            />
          </footer>
        )}
      </aside>
    </>
  );
}

function ActionButtons({
  status,
  pending,
  onAction,
}: {
  status: ModerationStatus;
  pending: boolean;
  onAction: (
    action: ModerationAction,
    config: { title: string; tone: "danger" | "success"; warning?: string },
  ) => void;
}) {
  const isCanceled = status === "CANCELED";
  const isBanned = status === "BANNED";
  // Estados irreversibles — no se pueden destrabar
  if (isCanceled || isBanned) {
    return (
      <span className="text-2xs text-fg-subtle italic">
        Este estado es irreversible. No hay más acciones disponibles.
      </span>
    );
  }

  return (
    <>
      {/* Reversas */}
      {status === "UNPUBLISHED" && (
        <button
          disabled={pending}
          onClick={() =>
            onAction("republish", {
              title: "Republicar evento",
              tone: "success",
              warning: "Vuelve a aparecer en el feed público.",
            })
          }
          className="nx-btn-secondary"
        >
          <RotateCcw size={14} /> Republicar
        </button>
      )}
      {status === "FROZEN" && (
        <button
          disabled={pending}
          onClick={() =>
            onAction("unfreeze", {
              title: "Descongelar ventas",
              tone: "success",
              warning: "Reanuda las ventas. El evento volverá a permitir checkout.",
            })
          }
          className="nx-btn-secondary"
        >
          <Play size={14} /> Descongelar
        </button>
      )}

      {/* Acciones reversibles */}
      {status === "NONE" && (
        <>
          <button
            disabled={pending}
            onClick={() =>
              onAction("unpublish", {
                title: "Despublicar evento",
                tone: "danger",
                warning:
                  "Sale del feed público pero el productor puede republicarlo editándolo. Reversible silencioso.",
              })
            }
            className="nx-btn-secondary"
          >
            <EyeOff size={14} /> Despublicar
          </button>
          <button
            disabled={pending}
            onClick={() =>
              onAction("freeze", {
                title: "Congelar ventas",
                tone: "danger",
                warning:
                  "El evento sigue visible pero las ventas se pausan (checkout devuelve 423). Reversible bloqueante.",
              })
            }
            className="nx-btn-secondary"
          >
            <Pause size={14} /> Congelar
          </button>
        </>
      )}

      {/* Acciones críticas — disponibles siempre que no esté ya en estado terminal */}
      <button
        disabled={pending}
        onClick={() =>
          onAction("cancel", {
            title: "Cancelar evento",
            tone: "danger",
            warning:
              "IRREVERSIBLE. La página pública mostrará overlay 'Cancelado'. Los reembolsos los gestiona el productor.",
          })
        }
        className="nx-btn-danger"
      >
        <CalendarOff size={14} /> Cancelar
      </button>
      <button
        disabled={pending}
        onClick={() =>
          onAction("ban", {
            title: "Banear evento",
            tone: "danger",
            warning:
              "IRREVERSIBLE y oculto. Desaparece para todos (público y productor). Solo Nexus puede verlo.",
          })
        }
        className="nx-btn-danger"
      >
        <Ban size={14} /> Banear
      </button>
    </>
  );
}
